import React from 'react';
import cn from 'classnames';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { UseFormTrigger } from 'react-hook-form';
import { Button } from '@react-md/button';
import { Typography } from '@react-md/typography';
import { useAppSelector } from '@app/hooks';
import { usePasswordRecoveryRequestCreateMutation } from '@services/df/auth';
import { selectAppCompanyName } from '@modules/app';
import { FeatureContent } from '@components/feature-content';
import { SignInForm } from '../types';
import { signInId } from '../helpers';

import styles from './reset-password.module.scss';

interface Props {
    email: string;
    trigger: UseFormTrigger<SignInForm>;
}

export const ResetPasswordButton: React.FC<Props> = ({ email, trigger }) => {
    const company = useAppSelector(selectAppCompanyName);
    const [send, { isLoading: isPasswordRecoverySending, isSuccess: isPasswordRecoverySuccess }] =
        usePasswordRecoveryRequestCreateMutation();

    const { pathname: redirect_path } = useLocation();

    const handleResetPassword = async (): Promise<void> => {
        const passedValidation = await trigger('email', { shouldFocus: true });
        if (passedValidation) {
            await send({
                requestResetPasswordRequest: { company_name: company, email, redirect_path },
            });
        }
    };

    const { t } = useTranslation();

    return (
        <div className={styles.box}>
            {!isPasswordRecoverySending && isPasswordRecoverySuccess ? (
                <Typography
                    type="subtitle-2"
                    weight="semi-bold"
                    component={'span'}
                    className={cn('rmd-typography--theme-success', 'input-addition-font-size')}
                >
                    {t('labels.resetPasswordSent', 'Email sent')}
                </Typography>
            ) : (
                <FeatureContent
                    contentKey={signInId('reset_password')}
                    fallback={t('labels.resetPassword', 'Reset')}
                    wrapper={props => (
                        <Button
                            theme="primary"
                            disableRipple
                            disabled={isPasswordRecoverySending}
                            className={cn(
                                'fade-in',
                                'rmd-button--flat',
                                'rmd-typography--semi-bold',
                                'input-addition-font-size',
                                styles.link,
                            )}
                            onClick={handleResetPassword}
                            {...props}
                        />
                    )}
                />
            )}
        </div>
    );
};
